import { Title, Flex, Box, Divider, Anchor } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { RoutePath, isMatchingCurrentPath, useMainRoutePath } from 'utils/location';
import { useLogout, usePatient } from 'hooks/api/useUser';
import { useAppContext } from 'context/hooks';
import { showFullName } from 'utils/helpers';
import { PortalButton } from 'components/Button/PortalButton';
import { BsBoxArrowInRight } from 'react-icons/bs';
import { useGroup } from 'hooks/api/useGroups';
import { Banner } from 'components/Banner/Banner';
import { sendActivationEmail } from 'services/user';
import { useCustomMutation } from 'hooks/api/useCustomMutation';

export const Header = () => {
  const { t } = useTranslation();
  const { mutate: logout } = useLogout();
  const { currentUser } = useAppContext();
  const { groupId, patientId } = useParams<{ groupId: string; patientId: string }>();
  const { data: groupData } = useGroup(groupId);
  const { data: patientData } = usePatient({ patientId });

  const currentRoutePath = useMainRoutePath();
  const location = useLocation();

  const renderTitleWithBreadCrumb = (breadCrumb: string) => {
    return (
      <Flex gap={'xs'}>
        <Anchor c={'var(--mantine-color-turquoise-7)'} href={`#/${currentRoutePath}`}>
          <Title order={2} fw={700}>
            {t(`navbar_${currentRoutePath}`)}
          </Title>
        </Anchor>
        <Title order={2} fw={700}>
          {`> ${breadCrumb}`}
        </Title>
      </Flex>
    );
  };

  const renderTitle = () => {
    if (isMatchingCurrentPath(RoutePath.PatientDetails, location) && patientData) {
      return renderTitleWithBreadCrumb(`${patientData.lastName}  ${patientData.firstName}`);
    }
    if (isMatchingCurrentPath(RoutePath.SettingsGroup, location) && groupData?.name) {
      return renderTitleWithBreadCrumb(groupData.name);
    }

    return (
      <Title order={2} fw={700}>
        {currentRoutePath === RoutePath.Profile ? showFullName(currentUser?.user) : t(`navbar_${currentRoutePath}`)}
      </Title>
    );
  };

  const { mutate: sendEmail } = useCustomMutation({
    fn: sendActivationEmail,
    onSuccess: { message: t('email_sent') },
  });

  const handleActivationButton = () => {
    if (currentUser?.user.email) {
      sendEmail({ email: currentUser?.user.email });
    }
  };

  return (
    <Box>
      {!currentUser?.user.activation && (
        <Banner
          message={t('account_not_activated')}
          bg={'var(--mantine-color-blue-5)'}
          action={
            <PortalButton
              variant={'outline'}
              color={'var(--mantine-color-white)'}
              label={t('resend_activation_link')}
              onClick={handleActivationButton}
            />
          }
        />
      )}
      <Flex justify={'space-between'} align={'flex-end'}>
        <Box>{renderTitle()}</Box>
        {isMatchingCurrentPath(RoutePath.Profile, location) && (
          <PortalButton
            Icon={BsBoxArrowInRight}
            label={t('log_out_label')}
            onClick={() => logout({})}
            testId={'button-logout'}
          />
        )}
      </Flex>
      <Divider mt={'sm'} mb={'xl'} />
    </Box>
  );
};
